import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, wsUrl, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Play, Square, UserPlus, Send, Trash2, Upload, Save, ExternalLink, Edit, Plus, Minus, Rocket, Swords, ScrollText, Users, MessageSquare, BookOpen, GripVertical, Check, Pin, Star, Skull } from "lucide-react";
import Markdown from "../components/Markdown";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TABS = [
  { id: "overview", label: "Overview", icon: BookOpen },
  { id: "party", label: "Party", icon: Users },
  { id: "npcs", label: "NPCs", icon: Users },
  { id: "ship", label: "Ship", icon: Rocket },
  { id: "initiative", label: "Initiative", icon: Swords },
  { id: "threads", label: "Threads", icon: Pin, gmOnly: true },
  { id: "timeline", label: "Timeline", icon: Star },
  { id: "sessions", label: "Sessions", icon: ScrollText },
  { id: "chat", label: "Chat", icon: MessageSquare },
];

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [users, setUsers] = useState([]);
  const [party, setParty] = useState([]);
  const [partyChars, setPartyChars] = useState([]);
  const [partyCharsnoNPC, setPartyCharsnoNPC] = useState([]);
  const [unassignedChars, setUnassignedChars] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [session, setSession] = useState(null);
  const [sessionsHistory, setSessionsHistory] = useState([]);
  const [ship, setShip] = useState(null);
  const [initiative, setInitiative] = useState([]);
  const [threads, setThreads] = useState([]);
  const [enemies, setEnemies] = useState([]);
  const [shipCatalog, setShipCatalog] = useState([]);
  const [showEnemyPicker, setShowEnemyPicker] = useState(false);
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", game_type: "Campaign" });
  const [newQ, setNewQ] = useState("");
  const [answers, setAnswers] = useState({});
  const [selectedChar, setSelectedChar] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [previewDesc, setPreviewDesc] = useState(false);
  const [partyCashDelta, setPartyCashDelta] = useState(0);
  const [showQuestionsMgr, setShowQuestionsMgr] = useState(false);
  const [npcName, setNpcName] = useState("");
  const [whisperTo, setWhisperTo] = useState("");
  const [whisperText, setWhisperText] = useState("");
  const [logEntry, setLogEntry] = useState({ type: "encounter", content: "", character_id: "" });
  const [summary, setSummary] = useState("");
  const wsRef = useRef(null);
  const imgInput = useRef(null);
  const chatLogRef = useRef(null);
  const scrollChatToBottom = () => {
    requestAnimationFrame(() => {
      if (chatLogRef.current) chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    });
  };

  const loadAll = async () => {
    try {
      const [camp, u, cs, qs, msgs, n, sh, ini, partyCharsResp] = await Promise.all([
        api.get(`/campaigns/${id}`), api.get("/users"), api.get("/characters"),
        api.get(`/questions?campaign_id=${id}`), api.get(`/chat/campaign/${id}/filtered`),
        api.get(`/campaigns/${id}/npcs`), api.get(`/campaigns/${id}/ship`), api.get(`/campaigns/${id}/initiative`),
        api.get(`/campaigns/${id}/characters`),
      ]);
      // Only the GM/admin can add characters, so only they need the unassigned list
      const canAddChars = camp.data.gm_id === user?.id || user?.role === "admin";
      const un = canAddChars ? await api.get("/characters/unassigned") : { data: [] };
      setC(camp.data); setUsers(u.data); setQuestions(qs.data); setMessages(msgs.data);
      setNpcs(n.data); setShip(sh.data); setInitiative(ini.data);
      setForm({ name: camp.data.name, description: camp.data.description, game_type: camp.data.game_type });
      const partyUsers = u.data.filter(us => camp.data.party_ids.includes(us.id));
      setParty(partyUsers);
      // Use campaign-scoped char list so non-GM/admin players still see party char names
      setPartyChars(partyCharsResp.data || [])
      setPartyCharsnoNPC((partyCharsResp.data || []).filter(ch => !ch.is_npc));
      setUnassignedChars(un.data.filter(ch => !ch.campaign_id && !ch.is_npc));
      try { const s = await api.get(`/campaigns/${id}/session/current`); setSession(s.data); if (s.data?.summary) setSummary(s.data.summary); } catch { setSession(null); }
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const loadHistory = async () => {
    try { const r = await api.get(`/campaigns/${id}/sessions`); setSessionsHistory(r.data); }
    catch { toast.error("Could not load history"); }
  };
  useEffect(() => { if (tab === "sessions") loadHistory(); if (tab === "chat") scrollChatToBottom(); if (tab === "threads" && isGM) loadThreads(); if (tab === "initiative" && isGM) loadEnemies(); if (tab === "ship" && isGM) loadShipCatalog(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { if (tab === "chat") scrollChatToBottom(); /* eslint-disable-next-line */ }, [messages]);

  useEffect(() => {
    if (!c) return;
    const canJoin = user?.role === "admin" || c.gm_id === user?.id || c.party_ids?.includes(user?.id);
    if (!canJoin) return;
    const ws = new WebSocket(wsUrl("campaign", id));
    ws.onmessage = (ev) => {
      const m = JSON.parse(ev.data);
      const isGm = c.gm_id === user?.id || user?.role === "admin";
      if (m.private_to_user_id && !isGm && m.private_to_user_id !== user?.id) return;
      setMessages(prev => [...prev, m]);
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [c, id, user]);

  if (!c) return <div className="p-8 mono">// Loading...</div>;

  const isGM = c.gm_id === user?.id || user?.role === "admin";
  const myChar = partyChars.find(ch => ch.owner_id === user?.id);
  const sessionPendingForMe = session && session.ended_at && user?.id && !((session.answers || {})[user?.id]) &&
    (!(session.attendance && Object.keys(session.attendance).length > 0) || (session.attendance || {})[user?.id]);
  const visibleTabs = TABS.filter(t => !t.gmOnly || isGM); // NPCs visible to all but cards only clickable for GM (handled below)
  
  const sendChat = () => {
    if (!chatText.trim() || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ content: chatText })); setChatText("");
  };
  const uploadImg = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5*1024*1024) { toast.error("Max 5MB"); return; }
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/campaigns/${id}/image`, fd, { headers:{"Content-Type":"multipart/form-data"}}); toast.success("Uploaded"); loadAll(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };
  const saveDetails = async () => { try { await api.put(`/campaigns/${id}`, form); toast.success("Saved"); setEditing(false); loadAll(); } catch { toast.error("Save failed"); } };
  const addCharacterToCamp = async (charId) => { try { await api.post(`/campaigns/${id}/character/add/${charId}`); toast.success("Added"); loadAll(); } catch { toast.error("Failed"); } };
  const removeMember = async (uid) => { try { await api.post(`/campaigns/${id}/party/remove/${uid}`); toast.success("Removed"); loadAll(); } catch { toast.error("Failed"); } };
  const startSession = async () => { try { await api.post(`/campaigns/${id}/session/start`); toast.success("Session started"); setAnswers({}); setSummary(""); loadAll(); } catch { toast.error("Failed"); } };

  const confirmAttendance = async (charId) => {
    if (!session?.id) { toast.error("No active session"); return; }
    try {
      await api.post(`/sessions/${session.id}/confirm?character_id=${charId}`);
      toast.success("Attendance confirmed");
      const s = await api.get(`/campaigns/${id}/session/current`); setSession(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const stopSession = async () => { try { await api.post(`/campaigns/${id}/session/stop`); toast.success("Session ended"); loadAll(); } catch { toast.error("Failed"); } };
  const openShop = async () => { try { await api.post(`/campaigns/${id}/shop/open`); toast.success("Store open"); loadAll(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const closeShop = async () => { try { await api.post(`/campaigns/${id}/shop/close`); toast.success("Store closed"); loadAll(); } catch { toast.error("Failed"); } };
  const endCampaign = async () => {
    if (!window.confirm(`End campaign "${c.name}"?\n\nIt becomes READ-ONLY. History preserved. Only a global admin can delete it later.`)) return;
    try { await api.post(`/campaigns/${id}/end`); toast.success("Campaign ended"); loadAll(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addQuestion = async () => { if (!newQ.trim()) return; try { await api.post("/questions", { text: newQ, scope:"campaign", campaign_id:id }); toast.success("Added"); setNewQ(""); loadAll(); } catch { toast.error("Failed"); } };
  const delQuestion = async (qid) => { try { await api.delete(`/questions/${qid}`); loadAll(); } catch {} };
  const submitAnswers = async () => {
    if (!session || !selectedChar) { toast.error("Pick your character"); return; }
    try { const r = await api.post(`/sessions/${session.id}/submit?character_id=${selectedChar}`, { answers });
      toast.success(`+${r.data.xp_gained} XP awarded`); setAnswers({}); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addPartyCash = async (delta) => {
    try { const r = await api.post(`/campaigns/${id}/cash`, { amount: delta });
      toast.success(`Party cash ${delta>=0?"+":""}${delta}`); setC({ ...c, party_cash: r.data.party_cash }); setPartyCashDelta(0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const createNpc = async () => {
    if (!npcName.trim()) return;
    try { await api.post(`/campaigns/${id}/npcs`, { name: npcName }); toast.success("NPC created"); setNpcName(""); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sendWhisper = async () => {
    if (!whisperTo || !whisperText.trim()) return;
    try { await api.post(`/characters/${whisperTo}/gm-message`, { content: whisperText }); toast.success("Whisper sent"); setWhisperText(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveShip = async (patch) => {
    try { const r = await api.put(`/campaigns/${id}/ship`, patch); setShip(r.data); toast.success("Ship saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const transferToShip = async (charId, charList, charIdx) => {
    try { await api.post(`/campaigns/${id}/ship/transfer`, { direction:"to_ship", character_id: charId, char_list: charList, char_index: charIdx });
      toast.success("Stowed"); loadAll(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const transferToChar = async (charId, shipIdx) => {
    try { await api.post(`/campaigns/${id}/ship/transfer`, { direction:"to_character", character_id: charId, ship_index: shipIdx });
      toast.success("Taken"); loadAll(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveInitiative = async (items, preserve = false) => {
    try { const r = await api.put(`/campaigns/${id}/initiative${preserve ? "?preserve_order=true" : ""}`, { items }); setInitiative(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const initHP = async (idx, delta) => {
    try { const r = await api.post(`/campaigns/${id}/initiative/${idx}/hp`, { delta });
      const next = [...initiative]; next[idx] = r.data; setInitiative(next); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addLogEntry = async () => {
    if (!session || !logEntry.content.trim()) return;
    try { await api.post(`/sessions/${session.id}/log`, logEntry); toast.success("Logged"); setLogEntry({...logEntry, content:""}); loadHistory(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveSummary = async () => {
    if (!session) return;
    try { await api.put(`/sessions/${session.id}/summary`, { summary }); toast.success("Summary saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Only show matches when the user actually types something — long lists are unwieldy
  const filteredUnassigned = search.trim()
    ? unassignedChars.filter(ch => ch.name.toLowerCase().includes(search.trim().toLowerCase()))
    : [];
 // ---- Plot Threads ----
  const loadThreads = async () => {
    try { const r = await api.get(`/campaigns/${id}/plot-threads`); setThreads(r.data); } catch {}
  };
  const createThread = async (title) => {
    if (!title?.trim()) return;
    try { await api.post(`/campaigns/${id}/plot-threads`, { title }); loadThreads(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const updateThread = async (tid, patch) => {
    try { const r = await api.put(`/plot-threads/${tid}`, patch);
      setThreads(threads.map(t => t.id === tid ? r.data : t)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteThread = async (tid) => {
    if (!window.confirm("Delete this plot thread?")) return;
    try { await api.delete(`/plot-threads/${tid}`); setThreads(threads.filter(t => t.id !== tid)); } catch {}
  };

  // ---- Enemies ----
  const loadEnemies = async () => {
    try { const r = await api.get("/enemies"); setEnemies(r.data); } catch {}
  };
  const loadShipCatalog = async () => {
    try {
      const [im, arm, up] = await Promise.all([
        api.get("/shop?item_type=internal_module"),
        api.get("/shop?item_type=armament"),
        api.get("/shop?item_type=upgrade"),
      ]);
      setShipCatalog([...im.data, ...arm.data, ...up.data]);
    } catch {}
  };
  const addEnemyToInitiative = (enemy) => {
    const newRow = {
      id: crypto.randomUUID(),
      name: enemy.name,
      type: "enemy",
      enemy_id: enemy.id,
      initiative: 10,
      hp: enemy.health,
      max_hp: enemy.health,
      armor: enemy.armor || 0,
      band: "Engaged",
      status: "",
    };
    const next = [...initiative, newRow];
    saveInitiative(next, true);
    setShowEnemyPicker(false);
    toast.success(`Added ${enemy.name} to initiative`);
  };

  // ---- Range band mover ----
  const moveBand = async (idx, band) => {
    const next = initiative.map((it, i) => i === idx ? { ...it, band } : it);
    saveInitiative(next, true);
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-4">
        {c.image_path ? (
          <img src={fileUrl(c.image_path)} alt="" className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover token-img" data-testid="camp-image" />
        ) : (
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border border-[rgba(255,176,0,0.4)] flex items-center justify-center mono text-xs text-[#A0A0A0]">NO IMG</div>
        )}
        <div className="flex-1 w-full">
          <h1 className="heading-mono text-2xl sm:text-4xl amber amber-glow uppercase flicker">{c.name}</h1>
          <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-widest uppercase">{c.game_type}</div>
          {c.ended && <div className="mt-2 inline-block mono text-[10px] bg-[#FF2A2A] text-black px-2 py-0.5 tracking-widest" data-testid="campaign-ended-badge">CAMPAIGN ENDED — READ ONLY</div>}
          {isGM && !c.ended && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>imgInput.current?.click()}><Upload size={12}/> Image</button>
              <input ref={imgInput} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={uploadImg} />
              <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>setEditing(!editing)}><Edit size={12}/> {editing ? "Close" : "Edit"}</button>
              <button className="text-xs mono text-[#FF2A2A] border border-[#FF2A2A] px-2 py-1 hover:bg-[rgba(255,42,42,0.1)]" onClick={endCampaign}>End Campaign</button>
            </div>
          )}
        </div>
        {isGM && !c.ended && (
          c.session_active ? (
            <button className="btn-amber flex items-center gap-2" onClick={stopSession} data-testid="stop-session-btn"><Square size={14}/> End Session</button>
          ) : (
            <button className="btn-amber flex items-center gap-2" onClick={startSession} data-testid="start-session-btn"><Play size={14}/> Start Session</button>
          )
        )}
        {!isGM && c.session_active && !c.ended && session && (
          <div className="flex flex-col gap-1" data-testid="attendance-cluster">
            {partyChars.filter(ch => ch.owner_id === user?.id && !ch.is_npc).map(ch => {
              const confirmed = ((session.attendance || {})[user?.id]?.character_ids || []).includes(ch.id);
              return (
                <button
                  key={ch.id}
                  className={`btn-amber flex items-center gap-2 text-xs ${confirmed ? "opacity-60" : ""}`}
                  disabled={confirmed}
                  onClick={()=>confirmAttendance(ch.id)}
                  data-testid={`confirm-attendance-${ch.id}`}
                >
                  <Check size={12}/> {confirmed ? `${ch.name} confirmed` : `Confirm: ${ch.name}`}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#333] overflow-x-auto" data-testid="campaign-tabs">
        {visibleTabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={()=>setTab(t.id)}
                    className={`px-3 py-2 mono text-xs uppercase tracking-widest whitespace-nowrap flex items-center gap-1 ${tab===t.id?"amber border-b-2 border-[#FFB000]":"text-[#A0A0A0] hover:amber"}`}>
              <Icon size={12} strokeWidth={1.5}/> {t.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="space-y-4">
          {editing && isGM && (
            <div className="panel p-5 space-y-3" data-testid="edit-campaign-panel">
              <div><label className="label-term mb-1 block">Name</label><input className="input-term" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
              <div><label className="label-term mb-1 block">Game Type</label>
                <select className="input-term" value={form.game_type} onChange={e=>setForm({...form,game_type:e.target.value})}><option>Campaign</option><option>Cinematic</option></select>
              </div>
              <div>
                <div className="flex items-center justify-between"><label className="label-term">Description (Markdown)</label>
                  <button type="button" className="btn-amber text-xs" onClick={()=>setPreviewDesc(!previewDesc)}>{previewDesc?"Edit":"Preview"}</button>
                </div>
                {previewDesc ? <div className="panel p-3 mt-1"><Markdown>{form.description}</Markdown></div>
                  : <textarea className="input-term min-h-[120px] mt-1" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} />}
              </div>
              <button className="btn-amber flex items-center gap-1" onClick={saveDetails}><Save size={14}/> Save</button>
            </div>
          )}
          <div className="panel p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="party-cash-widget">
            <div className="label-term amber">// Party Cash</div>
            <div className="heading-mono text-2xl neon-green">${c.party_cash || 0}</div>
            {isGM && !c.ended && (
              <div className="flex items-center gap-2 sm:ml-auto">
                <input type="number" className="input-term w-28" value={partyCashDelta} onChange={e=>setPartyCashDelta(Number(e.target.value))} />
                <button className="btn-amber" onClick={()=>addPartyCash(Math.abs(partyCashDelta))}><Plus size={12}/></button>
                <button className="btn-amber" onClick={()=>addPartyCash(-Math.abs(partyCashDelta))}><Minus size={12}/></button>
              </div>
            )}
          </div>
          <div className="panel p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="label-term amber">// Company Store</div>
            <div className={`mono text-sm ${c.shop_open ? "neon-green" : "text-[#A0A0A0]"}`}>{c.shop_open ? "● OPEN" : "○ CLOSED"}</div>
            {isGM && !c.ended && (
              <div className="flex gap-2 sm:ml-auto">
                {c.shop_open ? <button className="btn-amber" onClick={closeShop}>Close Store</button>
                  : <button className="btn-amber" onClick={openShop} disabled={!c.session_active}>{c.session_active ? "Open Store" : "Start session first"}</button>}
              </div>
            )}
          </div>
          <div className="panel p-5">
            <div className="label-term amber mb-2">// Campaign Data</div>
            <Markdown>{c.description}</Markdown>
            <div className="mt-3 mono text-xs text-[#A0A0A0]">GM: {users.find(u=>u.id===c.gm_id)?.username || "—"}</div>
            <div className="mono text-xs neon-green">Status: {c.session_active ? "Session Active" : (session?.ended_at ? "Questionnaire Open" : "Idle")}</div>
          </div>
          {/* GM Whisper */}
          {isGM && !c.ended && (
            <div className="panel p-5" data-testid="gm-whisper">
              <div className="label-term amber mb-3">// Private Whisper to Character</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <select className="input-term sm:max-w-xs" value={whisperTo} onChange={e=>setWhisperTo(e.target.value)}>
                  <option value="">// pick character</option>
                  {[...partyChars].map(ch => <option key={ch.id} value={ch.id}>{ch.name}{ch.is_npc?" (NPC)":""}</option>)}
                </select>
                <input className="input-term flex-1" placeholder="Private message..." value={whisperText} onChange={e=>setWhisperText(e.target.value)} />
                <button className="btn-amber" onClick={sendWhisper}><Send size={14}/></button>
              </div>
              <div className="mono text-[10px] text-[#A0A0A0] mt-2">// Visible only to that character's owner and the GM in chat.</div>
            </div>
          )}
        </div>
      )}

      {/* PARTY */}
      {tab === "party" && (
        <div className="space-y-4">
          <div className="panel p-5">
            <div className="label-term amber mb-3">// Party Members ({party.length})</div>
            <table className="w-full mono text-sm">
              <thead><tr className="text-left text-[#A0A0A0] text-xs uppercase"><th className="p-2 border-b border-[#333]">User</th><th className="p-2 border-b border-[#333]">Character</th>{isGM&&<th></th>}</tr></thead>
              <tbody>
                {party.map(p => {
                  const pc = partyChars.find(ch => ch.owner_id === p.id);
                  const canOpen = pc && (isGM || pc.owner_id === user?.id);
                  return (
                    <tr key={p.id}>
                      <td className="p-2 border-b border-[#222] amber">{p.username}</td>
                      <td className="p-2 border-b border-[#222]">{pc ? (
                        canOpen
                          ? <Link to={`/characters/${pc.id}`} className="amber hover:underline flex items-center gap-1">{pc.name} <ExternalLink size={10}/></Link>
                          : <span className="amber">{pc.name}</span>
                      ) : <span className="text-[#A0A0A0]">no character</span>}</td>
                      {isGM && !c.ended && <td className="p-2 border-b border-[#222] text-right"><button className="mono text-xs text-[#FF2A2A] hover:underline" onClick={()=>removeMember(p.id)}>remove</button></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {isGM && !c.ended && (
              <div className="mt-5">
                <div className="label-term mb-2">// Add Unassigned Characters</div>
                <input className="input-term mb-2" placeholder="type a character name to search..." value={search} onChange={e=>setSearch(e.target.value)} data-testid="add-char-search" />
                <div className="max-h-48 overflow-y-auto border border-[#222]">
                  {!search.trim() && <div className="p-3 mono text-xs text-[#A0A0A0]">// start typing to filter ({unassignedChars.length} unassigned characters)</div>}
                  {search.trim() && filteredUnassigned.length === 0 && <div className="p-3 mono text-xs text-[#A0A0A0]">// nothing matches</div>}
                  {filteredUnassigned.map(ch => (
                    <div key={ch.id} className="flex items-center justify-between p-2 border-b border-[#222]" data-testid={`add-char-row-${ch.id}`}>
                      <div><div className="amber mono">{ch.name}</div><div className="text-[10px] text-[#A0A0A0]">{ch.career} · {users.find(u=>u.id===ch.owner_id)?.username}</div></div>
                      <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>addCharacterToCamp(ch.id)}><UserPlus size={12}/> Add</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {sessionPendingForMe && myChar && (
            <div className="panel p-5 border-green" data-testid="pending-questionnaire">
              <div className="label-term neon-green mb-3">// XP Questionnaire — Submit to collect XP</div>
              <div className="space-y-2 mb-4">
                {questions.map(q => (
                  <div key={q.id} className="flex items-start gap-3 mono text-sm border-b border-[#222] pb-2">
                    <div className="flex-1">{q.text}</div>
                    <div className="flex gap-1">
                      <button className={`px-2 py-1 text-xs border ${answers[q.id]===true?"bg-[#4AF626] text-black":"border-[#4AF626] neon-green"}`} onClick={()=>setAnswers({...answers,[q.id]:true})}>YES +1</button>
                      <button className={`px-2 py-1 text-xs border ${answers[q.id]===false?"bg-[#555]":"border-[#555] text-[#A0A0A0]"}`} onClick={()=>setAnswers({...answers,[q.id]:false})}>NO</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <select className="input-term" value={selectedChar} onChange={e=>setSelectedChar(e.target.value)} data-testid="questionnaire-char-select">
                  <option value="">// pick your character</option>
                  {partyChars.filter(ch => {
                    if (ch.owner_id !== user?.id) return false;
                    const att = session?.attendance || {};
                    const tracked = Object.keys(att).length > 0;
                    if (!tracked) return true;
                    return (att[user?.id]?.character_ids || []).includes(ch.id);
                  }).map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
                </select>
                <button className="btn-amber" onClick={submitAnswers}>Submit & Collect XP</button>
              </div>
            </div>
          )}

          {isGM && !c.ended && (
            <div className="panel p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="label-term amber">// Custom Questions</div>
                <button className="btn-amber text-xs" onClick={()=>setShowQuestionsMgr(!showQuestionsMgr)}>{showQuestionsMgr?"Hide":"Manage"}</button>
              </div>
              {showQuestionsMgr && (
                <>
                  <div className="flex flex-col sm:flex-row gap-2 mb-3">
                    <input className="input-term flex-1" placeholder="Custom question..." value={newQ} onChange={e=>setNewQ(e.target.value)} />
                    <button className="btn-amber" onClick={addQuestion}>Add</button>
                  </div>
                  {questions.map(q => (
                    <div key={q.id} className="flex items-start gap-3 mono text-sm border-b border-[#222] pb-2">
                      <div className="flex-1"><div>{q.text}</div><div className="text-[10px] text-[#A0A0A0] uppercase">{q.scope}</div></div>
                      {q.scope === "campaign" && <button className="text-[#FF2A2A]" onClick={()=>delQuestion(q.id)}><Trash2 size={14}/></button>}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* NPCs */}
      {tab === "npcs" && (
        <div className="space-y-4">
          <div className="panel p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-term amber">// NPCs ({npcs.length})</div>
              {isGM && !c.ended && (
                <div className="flex gap-2">
                  <input className="input-term" placeholder="NPC name..." value={npcName} onChange={e=>setNpcName(e.target.value)} data-testid="npc-name-input" />
                  <button className="btn-amber flex items-center gap-1" onClick={createNpc} data-testid="create-npc-btn"><Plus size={12}/> Create NPC</button>
                </div>
              )}
            </div>
            {npcs.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no NPCs yet</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {npcs.map(n => {
                const card = (
                  <>
                    <div className="amber heading-mono uppercase">{n.name}</div>
                    <div className="mono text-xs text-[#A0A0A0]">{n.career || "NPC"} · ♥ {n.health}/{n.max_health}</div>
                  </>
                );
                return isGM ? (
                  <Link key={n.id} to={`/characters/${n.id}`} className="border border-[#222] p-3 hover:border-[#FFB000]" data-testid={`npc-card-${n.id}`}>
                    {card}
                  </Link>
                ) : (
                  <div key={n.id} className="border border-[#222] p-3 opacity-90 cursor-default" data-testid={`npc-card-${n.id}`} title="Only the GM can view this NPC's sheet">
                    {card}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* SHIP */}
      {tab === "ship" && ship && (
        <ShipPanel ship={ship} isGM={isGM && !c.ended} onSave={saveShip} partyChars={partyChars} onTransferToShip={transferToShip} onTransferToChar={transferToChar} ended={c.ended} shipCatalog={shipCatalog} />
      )}

      {/* INITIATIVE */}
      {tab === "initiative" && (
        <InitiativePanel items={initiative} setItems={setInitiative} isGM={isGM} canEdit={isGM && !c.ended && c.session_active} partyChars={partyChars} npcs={npcs} onSave={saveInitiative} onHP={initHP} onMoveBand={moveBand} enemies={enemies} onPickEnemy={addEnemyToInitiative} showPicker={showEnemyPicker} setShowPicker={setShowEnemyPicker} />
      )}

      {/* PLOT THREADS (GM only) */}
      {tab === "threads" && isGM && (
        <ThreadsPanel threads={threads} canEdit={!c.ended} onCreate={createThread} onUpdate={updateThread} onDelete={deleteThread} />
      )}

      {/* TIMELINE */}
      {tab === "timeline" && (
        <TimelinePanel campaignId={id} canLogMilestone={isGM && !c.ended && c.session_active} session={session} partyChars={partyChars} onMilestoneAdded={loadHistory} />
      )}

      {/* SESSIONS */}
      {tab === "sessions" && (
        <div className="space-y-4">
          {/* Session Log Add (GM, during active or ended-not-archived session) */}
          {isGM && session && !c.ended && (
            <div className="panel p-5" data-testid="log-entry">
              <div className="label-term amber mb-3">// Add Session Log Entry</div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <select className="input-term" value={logEntry.type} onChange={e=>setLogEntry({...logEntry, type:e.target.value})}>
                  <option value="encounter">Encounter</option>
                  <option value="death">Death</option>
                  <option value="injury">Injury</option>
                  <option value="purchase">Purchase</option>
                  <option value="note">Note</option>
                </select>
                <select className="input-term" value={logEntry.character_id||""} onChange={e=>setLogEntry({...logEntry, character_id:e.target.value})}>
                  <option value="">// no character</option>
                  {[...partyChars].map(ch => <option key={ch.id} value={ch.name}>{ch.name} {ch.is_npc ? "[NPC]" : ""}</option>)}
                </select>
                <input className="input-term sm:col-span-2" placeholder="Description..." value={logEntry.content} onChange={e=>setLogEntry({...logEntry, content:e.target.value})} />
              </div>
              <button className="btn-amber mt-2" onClick={addLogEntry}>Log it</button>
            </div>
          )}
          {isGM && session && !c.ended && (
            <div className="panel p-5">
              <div className="label-term amber mb-2">// End-of-Session Summary (Markdown)</div>
              <textarea className="input-term min-h-[120px]" value={summary} onChange={e=>setSummary(e.target.value)} placeholder="What happened this session..." />
              <button className="btn-amber mt-2 flex items-center gap-1" onClick={saveSummary}><Save size={14}/> Save Summary</button>
            </div>
          )}
          <SessionTimeline list={sessionsHistory} />
        </div>
      )}

      {/* CHAT */}
      {tab === "chat" && (
        <div className="panel p-5 flex flex-col h-[600px]" data-testid="campaign-chat">
          <div className="label-term amber mb-2">// Campaign Comms</div>
          <div ref={chatLogRef} className="chat-log flex-1 overflow-y-auto p-3 border border-[#222] neon-green">
            {messages.filter(m => !m.private_to_user_id).map(m => (
              <div key={m.id} className="mb-1">
                <span className="text-[#A0A0A0] mr-2">[{new Date(m.created_at).toLocaleTimeString()}]</span>
                <span className="amber">{m.display_name || m.username}:</span> <span style={{whiteSpace:"pre-wrap"}}>{m.content}</span>
              </div>
            ))}
            {messages.filter(m => !m.private_to_user_id).length === 0 && <div className="text-[#555]">// no transmissions</div>}
          </div>
          {!c.ended && (
            <div className="flex gap-2 mt-3">
              <input className="input-term flex-1" placeholder="> transmit..." value={chatText} onChange={e=>setChatText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendChat()} data-testid="chat-input" />
              <button className="btn-amber" onClick={sendChat} data-testid="chat-send-btn"><Send size={14}/></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ========== Sub-components ==========

function ShipPanel({ ship, isGM, onSave, partyChars, onTransferToShip, onTransferToChar, ended, shipCatalog }) {
  const [s, setS] = useState(ship);
  useEffect(() => setS(ship), [ship]);
  const NUM = [
    ["crew","Crew"],["passengers","Passengers"],["length","Length"],["ftl_rating","FTL Rating"],
    ["signature","Signature"],["thrusters","Thrusters"],["hull","Hull"],["armor","Armor"],["damage","Damage"]
  ];
  // Free-form tables (kept as text-box rows because they're campaign-local, not store-driven)
  const FREE_TBL = [
    ["minor_damage","Minor Component Damage",["name","effect"]],
    ["major_damage","Major Component Damage",["name","effect"]],
    ["ships_log","Ship's Log",["date","location"]],
  ];
  // Store-driven tables — GM picks from a dropdown populated from the store
  const CATALOG_TBL = [
    { key: "internal_modules", label: "Internal Modules", storeType: "internal_module",
      cols: [["module","Module"], ["size","Size"]],
      buildRow: (item) => ({ shop_id: item.id, module: item.name, size: item.stats?.size ?? 0 }) },
    { key: "upgrades", label: "Upgrades", storeType: "upgrade",
      cols: [["name","Name"], ["effect","Effect"]],
      buildRow: (item) => ({ shop_id: item.id, name: item.name, effect: item.stats?.effect || item.description || "" }) },
    { key: "armaments", label: "Armaments", storeType: "armament",
      cols: [["name","Name"], ["bonus","Bonus"], ["range","Range"], ["size","Size"]],
      buildRow: (item) => ({ shop_id: item.id, name: item.name, bonus: item.stats?.bonus ?? 0, range: item.stats?.range || "", size: item.stats?.size ?? 0 }) },
  ];
  const COL_LABEL = {
    module:"Module", size:"Size", name:"Name", effect:"Effect",
    bonus:"Bonus", range:"Range", date:"Date", location:"Location"
  };
  const set = (k,v) => setS({...s, [k]: v});

  const [picker, setPicker] = useState({}); // { [key]: shop_id }

  const catalogSection = (spec) => {
    const list = s[spec.key] || [];
    const options = (shipCatalog || []).filter(x => x.item_type === spec.storeType);
    const pickedId = picker[spec.key] || "";
    const chosen = options.find(o => o.id === pickedId);
    const addFromCatalog = () => {
      if (!chosen) return;
      set(spec.key, [...list, spec.buildRow(chosen)]);
      setPicker({...picker, [spec.key]: ""});
    };
    return (
      <div className="panel p-4" key={spec.key} data-testid={`ship-${spec.key}`}>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="label-term amber">// {spec.label} ({list.length})</div>
          {isGM && (
            <div className="flex gap-2 items-center">
              <select className="input-term" value={pickedId} onChange={e=>setPicker({...picker, [spec.key]: e.target.value})} data-testid={`ship-picker-${spec.key}`}>
                <option value="">// pick from store ({options.length} available)...</option>
                {options.map(o => <option key={o.id} value={o.id}>{o.name}{o.special ? " [SPECIAL]" : ""}</option>)}
              </select>
              <button className="btn-amber text-xs" disabled={!pickedId} onClick={addFromCatalog} data-testid={`ship-add-${spec.key}`}><Plus size={10}/> Add</button>
            </div>
          )}
        </div>
        {options.length === 0 && isGM && <div className="mono text-[10px] text-[#A0A0A0] mb-2">// no {spec.label.toLowerCase()} in the store yet — an admin can add them via the Store page</div>}
        {list.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// none equipped</div>}
        {list.length > 0 && (
          <div className="grid gap-2 mb-2 mono text-[10px] text-[#A0A0A0] uppercase tracking-widest" style={{gridTemplateColumns: `repeat(${spec.cols.length}, 1fr) auto`}}>
            {spec.cols.map(([col, label]) => <div key={col}>{label}</div>)}
            <div></div>
          </div>
        )}
        {list.map((row, i) => (
          <div key={i} className="grid gap-2 mb-2" style={{gridTemplateColumns: `repeat(${spec.cols.length}, 1fr) auto`}} data-testid={`ship-${spec.key}-row-${i}`}>
            {spec.cols.map(([col]) => (
              <div key={col} className="input-term flex items-center bg-transparent border-[#333]">{row[col] || ""}</div>
            ))}
            {isGM && <button className="text-[#FF2A2A]" onClick={()=>set(spec.key, list.filter((_,j)=>j!==i))} data-testid={`ship-remove-${spec.key}-${i}`}><Trash2 size={14}/></button>}
          </div>
        ))}
      </div>
    );
  };

  const freeTable = (key, fields) => {
    const list = s[key] || [];
    return (
      <div className="panel p-4" key={key} data-testid={`ship-${key}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="label-term amber">// {fields.label}</div>
          {isGM && <button className="btn-amber text-xs" onClick={()=>set(key, [...list, fields.cols.reduce((a,c)=>({...a,[c]:""}),{})])}><Plus size={10}/> Add Row</button>}
        </div>
        {list.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// none</div>}
        {list.length > 0 && (
          <div className="grid gap-2 mb-2 mono text-[10px] text-[#A0A0A0] uppercase tracking-widest" style={{gridTemplateColumns: `repeat(${fields.cols.length}, 1fr) auto`}}>
            {fields.cols.map(col => <div key={col}>{COL_LABEL[col] || col}</div>)}
            <div></div>
          </div>
        )}
        {list.map((row, i) => (
          <div key={i} className="grid gap-2 mb-2" style={{gridTemplateColumns: `repeat(${fields.cols.length}, 1fr) auto`}}>
            {fields.cols.map(col => (
              <input key={col} className="input-term" placeholder={COL_LABEL[col] || col} value={row[col]||""} disabled={!isGM}
                     onChange={e=>{const n=[...list]; n[i]={...row,[col]:e.target.value}; set(key, n);}} />
            ))}
            {isGM && <button className="text-[#FF2A2A]" onClick={()=>set(key, list.filter((_,j)=>j!==i))}><Trash2 size={14}/></button>}
          </div>
        ))}
      </div>
    );
  };
  return (
    <div className="space-y-4">
      <div className="panel p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="label-term mb-1 block">Name</label><input className="input-term" value={s.name||""} disabled={!isGM} onChange={e=>set("name",e.target.value)} /></div>
        <div><label className="label-term mb-1 block">Class</label><input className="input-term" value={s.manufacturer||""} disabled={!isGM} onChange={e=>set("manufacturer",e.target.value)} /></div>
        <div><label className="label-term mb-1 block">Size</label><input className="input-term" value={s.size||""} disabled={!isGM} onChange={e=>set("size",e.target.value)} /></div>
        <div><label className="label-term mb-1 block">AI</label><input className="input-term" value={s.ai||""} disabled={!isGM} onChange={e=>set("ai",e.target.value)} /></div>
        {NUM.map(([k,l]) => (
          <div key={k}><label className="label-term mb-1 block">{l}</label>
            <input className="input-term" type="number" value={s[k]||0} disabled={!isGM} onChange={e=>set(k,Number(e.target.value))} /></div>
        ))}
        <div className="sm:col-span-2">
          <label className="label-term mb-1 block">Notes</label>
          <textarea className="input-term min-h-[100px]" value={s.notes || ""} disabled={!isGM} onChange={e=>set("notes", e.target.value)} placeholder="Free-form notes about this ship..." data-testid="ship-notes" />
        </div>
        {isGM && <div className="sm:col-span-2"><button className="btn-amber flex items-center gap-1" onClick={()=>onSave(s)} data-testid="save-ship"><Save size={14}/> Save Ship</button></div>}
      </div>
      {CATALOG_TBL.map(spec => catalogSection(spec))}
      {FREE_TBL.map(([k,label,cols]) => freeTable(k, { label, cols }))}

      {/* Inventory */}
      <div className="panel p-5">
        <div className="label-term amber mb-3">// Ship Inventory ({(s.inventory||[]).length})</div>
        {(s.inventory||[]).length === 0 && <div className="mono text-xs text-[#A0A0A0]">// empty</div>}
        {(s.inventory||[]).map((it, i) => (
          <div key={i} className="flex items-center justify-between border-b border-[#222] py-2 gap-3 flex-wrap" data-testid={`ship-inv-${i}`}>
            <span className="mono">
              <span className="amber">{it.name}</span>
              {it._origin_list && <span className="ml-2 text-[9px] text-[#A0A0A0] uppercase">[{it._origin_list}]</span>}
              {it.weight ? <span className="ml-2 text-[10px] text-[#A0A0A0]">w{it.weight}</span> : null}
            </span>
            {!ended && partyChars.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="mono text-[10px] text-[#A0A0A0]">→ take to:</span>
                {partyChars.map(ch => (
                  <button key={ch.id} className="btn-amber text-xs" onClick={()=>onTransferToChar(ch.id, i)} data-testid={`take-${i}-${ch.id}`}>
                    {ch.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        <div className="mt-4 mono text-xs text-[#A0A0A0]">// To stow gear into the ship, go to a character's Inventory and use the "stow" button.</div>
      </div>
    </div>
  );
}

function SortableInitRow({ it, i, canEdit, onUpdate, onRemove, onHP }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <tr ref={setNodeRef} style={style} data-testid={`init-row-${i}`} className={isDragging ? "bg-[#1a1100]" : ""}>
      <td className="p-2 border-b border-[#222]">
        {canEdit ? (
          <button {...attributes} {...listeners} className="cursor-grab amber" data-testid={`init-drag-${i}`} aria-label="drag handle">
            <GripVertical size={14} />
          </button>
        ) : <span className="amber">{i+1}</span>}
      </td>
      <td className="p-2 border-b border-[#222] amber">{i+1}</td>
      <td className="p-2 border-b border-[#222]">
        <span className={it.type==="enemy"?"text-[#FF2A2A]":(it.type==="npc"?"text-[#A0A0A0]":"amber")}>{it.name}</span>
        <span className="ml-2 mono text-[9px] text-[#A0A0A0] uppercase">{it.type}</span>
      </td>
      <td className="p-2 border-b border-[#222]">
        {canEdit ? <input className="input-term w-16" type="number" value={it.initiative||0} onChange={e=>onUpdate(i,{initiative:Number(e.target.value)})} /> : <span className="neon-green">{it.initiative}</span>}
      </td>
      <td className="p-2 border-b border-[#222]">
        <span className="amber">{it.hp}/{it.max_hp}</span>
        {canEdit && (
          <span className="ml-2 inline-flex gap-1">
            <button className="btn-amber text-xs px-1.5 py-0.5" onClick={()=>onHP(i,1)}>+</button>
            <button className="btn-amber text-xs px-1.5 py-0.5" onClick={()=>onHP(i,-1)}>−</button>
          </span>
        )}
      </td>
      <td className="p-2 border-b border-[#222]">
        {canEdit ? <input className="input-term" placeholder="status" value={it.status||""} onChange={e=>onUpdate(i,{status:e.target.value})} /> : (it.status || "—")}
      </td>
      {canEdit && <td className="p-2 border-b border-[#222] text-right"><button className="text-[#FF2A2A]" onClick={()=>onRemove(i)}><Trash2 size={14}/></button></td>}
    </tr>
  );
}

function InitiativePanel({ items, isGM, canEdit, partyChars, npcs, onSave, onHP, onMoveBand, enemies, onPickEnemy, showPicker, setShowPicker }) {
  const [draft, setDraft] = useState(items);
  useEffect(() => setDraft(items), [items]);
  const [newRow, setNewRow] = useState({ name: "", initiative: 10, hp: 3, max_hp: 3, status: "", character_id: "" });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 }}));

  const addPartyChar = (ch) => {
    setDraft([...draft, { id: crypto.randomUUID(), name: ch.name, character_id: ch.id, type:"player", initiative:10, hp: ch.health, max_hp: ch.max_health, band: "Engaged", status:"" }]);
  };
  const addNpc = (ch) => {
    setDraft([...draft, { id: crypto.randomUUID(), name: ch.name, character_id: ch.id, type:"npc", initiative:10, hp: ch.health, max_hp: ch.max_health, band: "Engaged", status:"" }]);
  };
  const addEnemy = () => {
    if (!newRow.name) return;
    setDraft([...draft, { id: crypto.randomUUID(), ...newRow, type:"enemy", band: "Engaged" }]);
    setNewRow({ name: "", initiative: 10, hp: 3, max_hp: 3, status: "", character_id: "" });
  };
  const updateRow = (i, patch) => { const n=[...draft]; n[i]={...n[i], ...patch}; setDraft(n); };
  const removeRow = (i) => setDraft(draft.filter((_,j)=>j!==i));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = draft.findIndex(x => x.id === active.id);
    const newIdx = draft.findIndex(x => x.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(draft, oldIdx, newIdx);
    setDraft(reordered);
    onSave(reordered, true); // preserve_order=true
    toast.success("Order updated");
  };

  const rows = canEdit ? draft : items;

  return (
    <div className="space-y-4">
      {!canEdit && isGM && <div className="panel p-3 mono text-xs text-[#A0A0A0]" data-testid="init-readonly-note">// Editing requires an active session.</div>}

      {/* RANGE BAND KANBAN */}
      {items.length > 0 && (
        <RangeBandBoard items={items} canEdit={canEdit} onMoveBand={onMoveBand} onHP={onHP} />
      )}

      <div className="panel p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-term amber">// Combat Order ({items.length})</div>
          <div className="flex gap-2 items-center">
            {canEdit && <span className="mono text-[10px] text-[#A0A0A0]">// drag <GripVertical size={10} className="inline"/> to reorder</span>}
            {canEdit && <button className="btn-amber" onClick={()=>onSave(draft, false)} data-testid="save-initiative"><Save size={14}/> Sort by Init & Save</button>}
          </div>
        </div>
        {items.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no combatants</div>}
        {canEdit ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
              <table className="w-full mono text-sm">
                <thead><tr className="text-left text-[#A0A0A0] text-xs uppercase">
                  <th className="p-2 border-b border-[#333] w-8"></th>
                  <th className="p-2 border-b border-[#333]">#</th>
                  <th className="p-2 border-b border-[#333]">Name</th>
                  <th className="p-2 border-b border-[#333]">Init</th><th className="p-2 border-b border-[#333]">HP</th>
                  <th className="p-2 border-b border-[#333]">Status</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map((it, i) => (
                    <SortableInitRow key={it.id} it={it} i={i} canEdit={canEdit} onUpdate={updateRow} onRemove={removeRow} onHP={onHP} />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        ) : (
          <table className="w-full mono text-sm">
            <thead><tr className="text-left text-[#A0A0A0] text-xs uppercase">
              <th className="p-2 border-b border-[#333]">#</th>
              <th className="p-2 border-b border-[#333]">Name</th>
              <th className="p-2 border-b border-[#333]">Init</th><th className="p-2 border-b border-[#333]">HP</th>
              <th className="p-2 border-b border-[#333]">Status</th>
            </tr></thead>
            <tbody>
              {rows.map((it, i) => (
                <tr key={it.id || i} data-testid={`init-row-${i}`}>
                  <td className="p-2 border-b border-[#222] amber">{i+1}</td>
                  <td className="p-2 border-b border-[#222]">
                    <span className={it.type==="enemy"?"text-[#FF2A2A]":(it.type==="npc"?"text-[#A0A0A0]":"amber")}>{it.name}</span>
                    <span className="ml-2 mono text-[9px] text-[#A0A0A0] uppercase">{it.type}</span>
                  </td>
                  <td className="p-2 border-b border-[#222]"><span className="neon-green">{it.initiative}</span></td>
                  <td className="p-2 border-b border-[#222]"><span className="amber">{it.hp}/{it.max_hp}</span></td>
                  <td className="p-2 border-b border-[#222]">{it.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {canEdit && (
        <>
          <div className="panel p-5">
            <div className="label-term amber mb-3">// Add Party / NPCs</div>
            <div className="flex flex-wrap gap-2">
              {partyChars.map(ch => <button key={ch.id} className="btn-amber text-xs" onClick={()=>addPartyChar(ch)} data-testid={`add-init-pc-${ch.id}`}>+ {ch.name}</button>)}
              {npcs.map(ch => <button key={ch.id} className="btn-amber text-xs" onClick={()=>addNpc(ch)} data-testid={`add-init-npc-${ch.id}`}>+ {ch.name} (NPC)</button>)}
            </div>
          </div>
          <div className="panel p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-term amber">// Add Enemy</div>
              <button className="btn-amber text-xs" onClick={()=>setShowPicker(!showPicker)} data-testid="open-enemy-library"><Skull size={12} className="inline mr-1"/> {showPicker ? "Close Library" : "Open Library"}</button>
            </div>
            {showPicker && (
              <div className="mb-3 border border-[#222] p-2 max-h-72 overflow-y-auto" data-testid="enemy-library">
                {enemies.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// library is empty</div>}
                {enemies.map(e => (
                  <div key={e.id} className="flex items-center justify-between border-b border-[#1a1a1a] py-2 gap-2">
                    <div>
                      <div className="amber mono">{e.name} <span className="text-[9px] text-[#A0A0A0] uppercase ml-1">[{e.category}]</span></div>
                      <div className="mono text-[10px] text-[#A0A0A0]">HP {e.health} · ARM {e.armor} · SPD {e.speed} · {(e.attacks||[]).length} attack{(e.attacks||[]).length===1?"":"s"}</div>
                    </div>
                    <button className="btn-amber text-xs" onClick={()=>onPickEnemy(e)} data-testid={`pick-enemy-${e.id}`}><Plus size={12} className="inline"/> Add</button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <input className="input-term sm:col-span-2" placeholder="Custom Name" value={newRow.name} onChange={e=>setNewRow({...newRow, name:e.target.value})} />
              <input className="input-term" type="number" placeholder="Init" value={newRow.initiative} onChange={e=>setNewRow({...newRow, initiative:Number(e.target.value)})} />
              <input className="input-term" type="number" placeholder="HP" value={newRow.hp} onChange={e=>setNewRow({...newRow, hp:Number(e.target.value), max_hp:Number(e.target.value)})} />
              <button className="btn-amber" onClick={addEnemy} data-testid="add-enemy-btn">+ Custom</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SessionTimeline({ list }) {
  const TYPE_COLORS = { death: "text-[#FF2A2A]", injury: "text-[#FFB000]", encounter: "neon-green", purchase: "amber", note: "text-[#A0A0A0]" };
  return (
    <div className="panel p-5" data-testid="session-history">
      <div className="label-term amber mb-3">// Session History</div>
      {list.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no sessions yet</div>}
      <div className="space-y-3">
        {list.map((s, idx) => (
          <div key={s.id} className="border-l-2 border-[#FFB000] pl-4 pb-3 relative">
            <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[#FFB000]" />
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="heading-mono amber">SESSION #{list.length - idx}</span>
              <span className="mono text-[10px] text-[#A0A0A0]">started {new Date(s.started_at).toLocaleString()} by {s.started_by_name}</span>
              {!s.ended_at && <span className="mono text-[10px] neon-green">● in progress</span>}
            </div>
            {s.ended_at && <div className="mono text-[10px] text-[#A0A0A0] mt-0.5">ended {new Date(s.ended_at).toLocaleString()} · {Math.round((new Date(s.ended_at)-new Date(s.started_at))/60000)} min · Total XP +{s.total_xp}</div>}
            {s.summary && <div className="mt-2 panel p-3"><Markdown>{s.summary}</Markdown></div>}
            {s.answers_list?.length > 0 && (
              <table className="w-full mono text-xs mt-2">
                <thead><tr className="text-left text-[#A0A0A0]"><th className="py-1">Player</th><th className="py-1">Character</th><th className="py-1 text-right">XP</th></tr></thead>
                <tbody>
                  {s.answers_list.map(a => <tr key={a.user_id}><td className="py-0.5 amber">{a.username}</td><td className="py-0.5">{a.character_name}</td><td className="py-0.5 text-right neon-green">+{a.xp}</td></tr>)}
                </tbody>
              </table>
            )}
            {(s.log||[]).length > 0 && (
              <div className="mt-2 space-y-1">
                {s.log.map(e => (
                  <div key={e.id} className="mono text-xs flex gap-2">
                    <span className={`uppercase ${TYPE_COLORS[e.type] || "amber"}`}>[{e.type}]</span>
                    <span className="text-[#A0A0A0]">{new Date(e.created_at).toLocaleTimeString()}</span>
                    <span className="flex-1">{e.character_id} {e.content}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const RANGE_BANDS = ["Engaged", "Short", "Medium", "Long", "Extreme"];
const BAND_TONE = {
  Engaged: "border-[#FF2A2A] text-[#FF2A2A]",
  Short: "border-[#FFB000] text-[#FFB000]",
  Medium: "border-[#4AF626] text-[#4AF626]",
  Long: "border-[#4FC3F7] text-[#4FC3F7]",
  Extreme: "border-[#A0A0A0] text-[#A0A0A0]",
};

function RangeBandCard({ it, idx, canEdit, onMoveBand, onHP }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const tone = it.type === "enemy" ? "text-[#FF2A2A]" : (it.type === "npc" ? "text-[#A0A0A0]" : "amber");
  return (
    <div ref={setNodeRef} style={style} data-testid={`band-card-${it.id}`} className="border border-[#222] p-2 mb-2 bg-[#0a0a0a]">
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className={`mono text-xs truncate ${tone}`}>
            <span className="mono text-[9px] text-[#FFB000] mr-1">#{idx + 1}</span>
            {it.name}
          </div>
          <div className="mono text-[10px] text-[#A0A0A0] mt-0.5">HP {it.hp}/{it.max_hp} · Init {it.initiative}</div>
          {it.status && <div className="mono text-[9px] text-[#A0A0A0] truncate">{it.status}</div>}
        </div>
        {canEdit && (
          <button {...attributes} {...listeners} className="cursor-grab amber" data-testid={`band-drag-${it.id}`} aria-label="drag handle">
            <GripVertical size={12} />
          </button>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center justify-between gap-1 mt-1">
          <div className="flex gap-1">
            <button className="btn-amber text-[10px] px-1 py-0" onClick={()=>onHP(idx, -1)}>−</button>
            <button className="btn-amber text-[10px] px-1 py-0" onClick={()=>onHP(idx, 1)}>+</button>
          </div>
          <select className="input-term text-[10px] py-0 px-1" value={it.band || "Engaged"} onChange={e=>onMoveBand(idx, e.target.value)} data-testid={`band-select-${it.id}`}>
            {RANGE_BANDS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function RangeBandColumn({ band, items, canEdit, onMoveBand, onHP }) {
  const { setNodeRef, isOver } = useDroppable({ id: `band-${band}` });
  const bandItems = items.filter(it => (it.band || "Engaged") === band);
  return (
    <div className="flex-1 min-w-[170px]">
      <div className={`mono text-xs uppercase tracking-widest border ${BAND_TONE[band]} px-2 py-1 mb-2 flex items-center justify-between`}>
        <span>{band}</span>
        <span className="text-[10px] text-[#A0A0A0]">{bandItems.length}</span>
      </div>
      <div ref={setNodeRef} className={`min-h-[120px] p-1 ${isOver ? "bg-[#1a1100]" : ""}`} data-testid={`band-col-${band}`}>
        <SortableContext items={bandItems.map(b => b.id)} strategy={verticalListSortingStrategy}>
          {bandItems.map(it => {
            const absoluteIdx = items.findIndex(x => x.id === it.id);
            return <RangeBandCard key={it.id} it={it} idx={absoluteIdx} canEdit={canEdit} onMoveBand={onMoveBand} onHP={onHP} />;
          })}
        </SortableContext>
      </div>
    </div>
  );
}

function RangeBandBoard({ items, canEdit, onMoveBand, onHP }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 }}));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const overId = String(over.id);
    // Find target band
    let targetBand = null;
    if (overId.startsWith("band-")) {
      targetBand = overId.slice(5);
    } else {
      const target = items.find(it => it.id === over.id);
      if (target) targetBand = target.band || "Engaged";
    }
    if (!targetBand) return;
    const movingIdx = items.findIndex(it => it.id === active.id);
    if (movingIdx === -1) return;
    if ((items[movingIdx].band || "Engaged") === targetBand) return;
    onMoveBand(movingIdx, targetBand);
  };

  return (
    <div className="panel p-5" data-testid="range-band-board">
      <div className="flex items-center justify-between mb-3">
        <div className="label-term amber">// Range Bands</div>
        <div className="mono text-[10px] text-[#A0A0A0]">{canEdit ? "// drag cards between bands or use the dropdown" : "// view-only"}</div>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto">
          {RANGE_BANDS.map(b => (
            <RangeBandColumn key={b} band={b} items={items} canEdit={canEdit} onMoveBand={onMoveBand} onHP={onHP} />
          ))}
        </div>
      </DndContext>
    </div>
  );
}

const THREAD_STATUSES = ["Open", "Resolved", "Failed"];
const STATUS_TONE = { Open: "text-[#FFB000]", Resolved: "text-[#4AF626]", Failed: "text-[#FF2A2A]" };

function ThreadsPanel({ threads, canEdit, onCreate, onUpdate, onDelete }) {
  const [newTitle, setNewTitle] = useState("");
  const [filter, setFilter] = useState("all");

  const filtered = threads.filter(t => filter === "all" || t.status === filter);

  return (
    <div className="space-y-4" data-testid="threads-panel">
      {canEdit && (
        <div className="panel p-4 flex gap-2 items-end">
          <div className="flex-1">
            <label className="label-term mb-1 block">New Plot Thread</label>
            <input className="input-term" placeholder="e.g. The missing officer's locker..." value={newTitle} onChange={e=>setNewTitle(e.target.value)} onKeyDown={e=>e.key==="Enter"&&(onCreate(newTitle), setNewTitle(""))} data-testid="new-thread-input" />
          </div>
          <button className="btn-amber" onClick={()=>{ onCreate(newTitle); setNewTitle(""); }} data-testid="new-thread-btn"><Plus size={14}/> Add</button>
        </div>
      )}

      <div className="flex gap-1 border-b border-[#333]">
        {["all", ...THREAD_STATUSES].map(s => (
          <button key={s} onClick={()=>setFilter(s)} data-testid={`thread-filter-${s}`} className={`px-3 py-1 mono text-xs uppercase tracking-widest ${filter === s ? "amber border-b-2 border-[#FFB000]" : "text-[#A0A0A0]"}`}>{s}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.length === 0 && <div className="mono text-xs text-[#A0A0A0] col-span-2 p-4">// no plot threads</div>}
        {filtered.map(t => (
          <ThreadCard key={t.id} thread={t} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function ThreadCard({ thread, canEdit, onUpdate, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [body, setBody] = useState(thread.body || "");
  const [title, setTitle] = useState(thread.title);
  const [tag, setTag] = useState(thread.tag || "");
  const save = () => { onUpdate(thread.id, { title, body, tag }); setEdit(false); };

  return (
    <div className={`panel p-4 ${thread.pinned ? "border-[#FFB000]" : ""}`} data-testid={`thread-${thread.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          {edit ? (
            <input className="input-term mb-2" value={title} onChange={e=>setTitle(e.target.value)} data-testid={`thread-edit-title-${thread.id}`} />
          ) : (
            <div className="heading-mono amber uppercase">{thread.pinned && <Pin size={12} className="inline mr-1"/>}{thread.title}</div>
          )}
          <div className="flex items-center gap-2 mt-1">
            <select className="input-term text-xs py-0" value={thread.status} disabled={!canEdit} onChange={e=>onUpdate(thread.id, { status: e.target.value })} data-testid={`thread-status-${thread.id}`}>
              {THREAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className={`mono text-[9px] uppercase tracking-widest ${STATUS_TONE[thread.status]}`}>● {thread.status}</span>
            {edit ? (
              <input className="input-term text-xs py-0 flex-1" placeholder="tag" value={tag} onChange={e=>setTag(e.target.value)} />
            ) : (
              thread.tag && <span className="mono text-[9px] border border-[#333] px-1.5 py-0.5">{thread.tag}</span>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-col gap-1">
            <button className="text-[#FFB000]" title="pin" onClick={()=>onUpdate(thread.id, { pinned: !thread.pinned })} data-testid={`thread-pin-${thread.id}`}><Pin size={12} className={thread.pinned ? "" : "opacity-40"}/></button>
            <button className="text-[#FFB000]" title="edit" onClick={()=>setEdit(!edit)} data-testid={`thread-edit-${thread.id}`}><Edit size={12}/></button>
            <button className="text-[#FF2A2A]" title="delete" onClick={()=>onDelete(thread.id)} data-testid={`thread-delete-${thread.id}`}><Trash2 size={12}/></button>
          </div>
        )}
      </div>
      {edit ? (
        <>
          <textarea className="input-term min-h-[120px]" placeholder="Body (Markdown supported)" value={body} onChange={e=>setBody(e.target.value)} data-testid={`thread-edit-body-${thread.id}`} />
          <div className="flex gap-2 justify-end mt-2">
            <button className="btn-amber text-xs" onClick={()=>setEdit(false)}>Cancel</button>
            <button className="btn-amber text-xs" onClick={save} data-testid={`thread-save-${thread.id}`}><Save size={12}/> Save</button>
          </div>
        </>
      ) : (
        thread.body && <div className="mt-1 mono text-xs text-[#A0A0A0]"><Markdown>{thread.body}</Markdown></div>
      )}
    </div>
  );
}

const MILESTONE_TYPES = ["milestone", "death", "injury", "purchase", "encounter", "no_show", "note"];
const MILESTONE_TONE = {
  milestone: "border-[#FFB000] bg-[#1a1100]",
  death: "border-[#FF2A2A]",
  injury: "border-[#FFB000]",
  encounter: "border-[#4AF626]",
  purchase: "border-[#A0A0A0]",
  no_show: "border-[#666]",
  note: "border-[#444]",
};

function TimelinePanel({ campaignId, canLogMilestone, session, partyChars, onMilestoneAdded }) {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [show, setShow] = useState(false);
  const [milestone, setMilestone] = useState({ type: "milestone", content: "", character_id: "" });

  const load = async () => {
    try { const r = await api.get(`/campaigns/${campaignId}/sessions`); setSessions(r.data); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  // Flatten all log entries across sessions into a single timeline
  const entries = [];
  sessions.forEach((s, sIdx) => {
    const num = sessions.length - sIdx;
    if (s.summary) entries.push({ id: `sum-${s.id}`, type: "summary", content: s.summary, session_num: num, ts: s.ended_at || s.started_at });
    (s.log || []).forEach(e => entries.push({ ...e, session_num: num, ts: e.created_at }));
  });
  entries.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  const filtered = filter === "all" ? entries
    : filter === "milestone" ? entries.filter(e => e.type === "milestone" || e.type === "summary")
    : entries.filter(e => e.type === filter);

  const submitMilestone = async () => {
    if (!session?.id || !milestone.content.trim()) return;
    try {
      await api.post(`/sessions/${session.id}/log`, milestone);
      toast.success("Milestone logged");
      setMilestone({ type: "milestone", content: "", character_id: "" });
      setShow(false);
      load();
      onMilestoneAdded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="timeline-panel">
      {canLogMilestone && (
        <div className="panel p-4">
          <div className="flex items-center justify-between">
            <div className="label-term amber">// Log a Milestone</div>
            <button className="btn-amber text-xs" onClick={()=>setShow(show)} data-testid="toggle-milestone-form">{show ? "Close" : "Add Milestone"}</button>
          </div>
          {show && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
              <select className="input-term" value={milestone.type} onChange={e=>setMilestone({...milestone, type: e.target.value})} data-testid="milestone-type">
                {MILESTONE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="input-term" value={milestone.character_id} onChange={e=>setMilestone({...milestone, character_id: e.target.value})} data-testid="milestone-char">
                <option value="">// (no character)</option>
                {partyChars.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
              </select>
              <input className="input-term" placeholder="e.g. Reached LV 4 · Killed Queen · etc" value={milestone.content} onChange={e=>setMilestone({...milestone, content: e.target.value})} data-testid="milestone-content" />
              <button className="btn-amber" onClick={submitMilestone} data-testid="milestone-submit"><Plus size={14}/> Log</button>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-1 border-b border-[#333] overflow-x-auto">
        {["all", "milestone", "death", "injury", "encounter", "purchase", "no_show", "note"].map(s => (
          <button key={s} onClick={()=>setFilter(s)} data-testid={`timeline-filter-${s}`} className={`px-3 py-1 mono text-xs uppercase tracking-widest whitespace-nowrap ${filter === s ? "amber border-b-2 border-[#FFB000]" : "text-[#A0A0A0]"}`}>{s}</button>
        ))}
      </div>

      <div className="panel p-5" data-testid="timeline-list">
        {filtered.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// nothing logged yet</div>}
        <div className="space-y-2">
          {filtered.map(e => (
            <div key={e.id} className={`border-l-4 ${MILESTONE_TONE[e.type] || "border-[#333]"} pl-3 py-1.5 ${e.type === "milestone" ? "bg-[#1a1100]" : ""}`} data-testid={`timeline-entry-${e.id}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className={`mono text-[10px] uppercase tracking-widest ${e.type === "milestone" ? "neon-green" : "text-[#A0A0A0]"}`}>
                  {e.type === "milestone" && <Star size={10} className="inline mr-1"/>}
                  [{e.type}]
                </span>
                <span className="mono text-[10px] text-[#A0A0A0]">SESSION #{e.session_num}</span>
                <span className="mono text-[9px] text-[#666]">{new Date(e.ts).toLocaleString()}</span>
              </div>
              <div className="mono text-xs mt-0.5">{e.character_id} {e.content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}